import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LastWorkoutSummaryCard } from '../components/workout/LastWorkoutSummaryCard';
import { AffiliatePlacement } from '../components/AffiliatePlacement';
import { PremiumCard, PremiumPage, PrimaryButton } from '../components/premium';
import { useAuth } from '../auth/AuthContext';
import { api } from '../lib/api';
import { localDateKey, localDateKeyFromIso } from '../lib/dateKey';
import { findPlanDayIndex, isLikelyRestOrConditioning } from '../lib/planDayUtils';
import { getTrainingPrefs, isScheduledRecoveryDay } from '../lib/trainingPrefs';
import {
  formatVolumeKg,
  sortWorkoutsNewest,
  weeklyVolumeKg,
  workoutsThisWeek,
  workoutSetCount,
} from '../lib/workoutStats';
import { workoutDayStreakFromIso } from '../lib/workoutStreak';
import { isNativeIOS } from '../lib/platform';
import {
  formatHealthSyncSuccess,
  isAppleHealthAuthorized,
  syncAppleHealth,
} from '../lib/appleHealth';
import { draftHasWork, loadWorkoutSessionDraft } from '../lib/workoutSessionDraft';
import type { SavedProgramData, WorkoutPlanResult } from '../coachTypes';
import type { UserProfile, Workout } from '../types';

function TodayIcon({ type }: { type: 'today' | 'flame' | 'streak' | 'schedule' }) {
  const paths = {
    today: (
      <>
        <rect x="3" y="5" width="18" height="16" rx="2.5" />
        <path d="M8 3v4M16 3v4M3 10h18" />
      </>
    ),
    flame: <path d="M12 22c4-3 6-6 6-10a6 6 0 0 0-12 0c0 4 2 7 6 10z" />,
    streak: <path d="M13 2 4 14h7l-1 8 10-14h-7l0-6z" />,
    schedule: (
      <>
        <rect x="3" y="5" width="18" height="16" rx="2.5" />
        <path d="M8 3v4M16 3v4M3 10h18M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01" />
      </>
    ),
  };
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      {paths[type]}
    </svg>
  );
}

function TodayStatTile({
  icon,
  label,
  value,
  detail,
  accent,
}: {
  icon: 'today' | 'flame' | 'streak' | 'schedule' | 'steps';
  label: string;
  value: string;
  detail?: string;
  accent?: string;
}) {
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-1.5 rounded-xl border border-white/[0.06] bg-white/[0.04] px-2.5 py-2.5">
      <div className={`flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)] ${accent ?? ''}`}>
        <span className={accent ?? 'text-[var(--text-muted)]'}>
          <TodayIcon type={icon === 'steps' ? 'schedule' : icon} />
        </span>
        {label}
      </div>
      <div className={`truncate text-sm font-bold tabular-nums leading-tight ${accent ? accent : 'text-[var(--text-primary)]'}`}>
        {value}
      </div>
      {detail ? <div className="truncate text-[10px] text-[var(--text-muted)]">{detail}</div> : null}
    </div>
  );
}

export function WorkoutsPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const imperial = user?.preferredUnitSystem === 'imperial';
  const trainingPrefs = getTrainingPrefs(user?.id);
  const scheduleTarget = trainingPrefs.daysPerWeek ?? 4;

  const [list, setList] = useState<Workout[]>([]);
  const [activeGuide, setActiveGuide] = useState<{ title: string; plan: WorkoutPlanResult } | null>(null);
  const [healthSteps, setHealthSteps] = useState<number | null>(null);
  const [healthActiveCalories, setHealthActiveCalories] = useState<number | null>(null);
  const [healthSyncing, setHealthSyncing] = useState(false);
  const [healthAuthorized, setHealthAuthorized] = useState(() => isAppleHealthAuthorized());
  const [healthToast, setHealthToast] = useState<string | null>(null);
  const [resumeDraft, setResumeDraft] = useState<ReturnType<typeof loadWorkoutSessionDraft>>(null);
  const todayKey = useMemo(() => localDateKey(), []);

  function showHealthToast(msg: string) {
    setHealthToast(msg);
    window.setTimeout(() => setHealthToast(null), 4200);
  }

  const refresh = useCallback(() => {
    api<Workout[]>('/api/workouts').then(setList).catch(() => setList([]));
  }, []);

  const loadTodayActivity = useCallback(async () => {
    try {
      const day = await api<{
        healthSteps?: number | null;
        healthActiveCalories?: number | null;
      }>(`/api/nutrition/day/${todayKey}`);
      setHealthSteps(day.healthSteps ?? null);
      setHealthActiveCalories(day.healthActiveCalories ?? null);
    } catch {
      setHealthSteps(null);
      setHealthActiveCalories(null);
    }
  }, [todayKey]);

  const reloadProgram = useCallback(async () => {
    try {
      const prof = await api<UserProfile | null>('/api/profile');
      const aid = prof?.activeSavedWorkoutPlanId;
      if (aid) {
        const row = await api<{ title: string; data: SavedProgramData }>(`/api/saved-workout-plans/${aid}`);
        if (row.data?.plan) setActiveGuide({ title: row.title, plan: row.data.plan });
        else setActiveGuide(null);
      } else setActiveGuide(null);
    } catch {
      setActiveGuide(null);
    }
  }, []);

  useEffect(() => {
    refresh();
    void reloadProgram();
    void loadTodayActivity();
    setResumeDraft(loadWorkoutSessionDraft(user?.id));
  }, [refresh, reloadProgram, loadTodayActivity, user?.id]);

  useEffect(() => {
    // Do not auto-request HealthKit on page load — concurrent auth hangs and the
    // permission sheet never appears, so the app never shows in Health settings.
    if (!isNativeIOS() || !user || !isAppleHealthAuthorized()) return;
    let cancelled = false;
    void (async () => {
      try {
        const result = await syncAppleHealth();
        if (cancelled) return;
        setHealthSteps(result.steps);
        setHealthActiveCalories(result.activeCalories);
        setHealthAuthorized(true);
        if (result.importedWorkouts > 0) refresh();
      } catch {
        /* quiet background refresh */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, refresh]);

  const sorted = useMemo(() => sortWorkoutsNewest(list), [list]);
  const lastWorkout = sorted[0] ?? null;

  const todayEntry = useMemo(() => {
    if (!activeGuide) return null;
    const idx = findPlanDayIndex(activeGuide.plan.days, new Date());
    return idx != null ? activeGuide.plan.days[idx] : null;
  }, [activeGuide]);

  const planRestDay = Boolean(todayEntry && isLikelyRestOrConditioning(todayEntry));
  const recoveryDay = isScheduledRecoveryDay(planRestDay, trainingPrefs);

  const todaySession = useMemo(() => {
    if (!todayEntry || planRestDay) return null;
    const exercises = todayEntry.exercises.length;
    const sets = todayEntry.exercises.reduce((s, e) => s + (Number(e.sets) || 0), 0);
    const duration = Math.min(120, Math.max(35, exercises * 10 + sets * 2));
    return { exercises, sets, duration, focus: todayEntry.focus, day: todayEntry.day, muscles: todayEntry.focus || todayEntry.day };
  }, [todayEntry, planRestDay]);

  const workoutsToday = useMemo(
    () => list.filter((w) => localDateKeyFromIso(w.startedAt) === todayKey),
    [list, todayKey],
  );

  const todayComplete = workoutsToday.length > 0;
  const streak = workoutDayStreakFromIso(list.map((w) => w.startedAt));
  const weekWorkouts = workoutsThisWeek(list);
  const weekVolKg = weeklyVolumeKg(list, Date.now() - 7 * 86400000);
  const weekVolLabel = formatVolumeKg(weekVolKg, imperial);
  const weekSets = weekWorkouts.reduce((s, w) => s + workoutSetCount(w), 0);
  const loggedKcal = workoutsToday.reduce((s, w) => s + (w.estimatedCaloriesBurned ?? 0), 0);
  const todayKcal = healthActiveCalories ?? loggedKcal;
  const healthConnected =
    healthAuthorized || healthActiveCalories != null || healthSteps != null;

  const todayLabel = todayComplete ? 'Complete' : recoveryDay ? 'Recovery' : 'Workout day';
  const burnedLabel =
    todayKcal > 0
      ? `${todayKcal.toLocaleString()} kcal`
      : healthSyncing
        ? 'Syncing…'
        : recoveryDay
          ? '—'
          : '0 kcal';
  const burnedDetail =
    healthConnected && healthActiveCalories != null ? 'Apple Health active energy' : undefined;
  const stepsLabel =
    healthSteps != null
      ? healthSteps.toLocaleString()
      : healthSyncing
        ? '…'
        : '—';
  const streakLabel = `${streak} ${streak === 1 ? 'day' : 'days'}`;
  const scheduleLabel = `${scheduleTarget} days/wk`;

  const weekProgress = scheduleTarget > 0 ? Math.min(1, weekWorkouts.length / scheduleTarget) : 0;

  const lastWorkoutHint = useMemo(() => {
    if (!lastWorkout || todayComplete) return null;
    const sets = workoutSetCount(lastWorkout);
    const dur = lastWorkout.durationMinutes;
    const parts: string[] = [];
    if (dur && dur > 0) parts.push(`${dur} min`);
    if (sets > 0) parts.push(`${sets} sets`);
    if (!parts.length) return null;
    return `Last: ${parts.join(' · ')} — beat it today`;
  }, [lastWorkout, todayComplete]);

  function startSession() {
    nav('/workouts/session', {
      state: todayEntry && !planRestDay
        ? {
            prefillName: todaySession?.focus || todayEntry.focus || activeGuide?.title,
            prefillExercises: todayEntry.exercises.map((e) => e.name),
          }
        : undefined,
    });
  }

  function startRecoveryLog() {
    nav('/workouts/session', { state: { prefillName: 'Recovery · Mobility' } });
  }

  const todayWorkoutId = workoutsToday[0]?.id;

  return (
    <PremiumPage title="Train" className="space-y-3">
      {resumeDraft && draftHasWork(resumeDraft) ? (
        <PremiumCard variant="train" className="!p-4">
          <p className="text-[10px] font-bold uppercase tracking-widest text-sky-300/90">In progress</p>
          <h2 className="mt-1 font-display text-lg font-bold text-[var(--text-primary)]">Resume your workout</h2>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">
            {resumeDraft.workoutName.trim() || 'Unnamed session'} · {resumeDraft.exercises.filter((e) => e.name.trim()).length}{' '}
            exercise{resumeDraft.exercises.filter((e) => e.name.trim()).length === 1 ? '' : 's'}
          </p>
          <PrimaryButton className="mt-4 !min-h-[48px]" fullWidth onClick={() => nav('/workouts/session')}>
            Continue logging
          </PrimaryButton>
        </PremiumCard>
      ) : null}

      <AffiliatePlacement slot="inline" />

      {/* 1 — Hero */}
      {todayComplete ? (
        <PremiumCard variant="success" className="!p-5 shadow-[0_12px_40px_rgba(34,197,94,0.12)]">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-300/90">Today</p>
              <h2 className="mt-1 font-display text-2xl font-bold text-[var(--text-primary)]">Session complete</h2>
              <p className="mt-1 text-sm text-[var(--text-secondary)]">You logged today&apos;s training.</p>
            </div>
            {streak > 0 ? (
              <span className="shrink-0 rounded-full border border-amber-400/30 bg-amber-500/15 px-2.5 py-1 text-[10px] font-bold text-amber-200">
                {streak}d streak
              </span>
            ) : null}
          </div>
          <div className="mt-5 flex flex-col gap-2">
            {todayWorkoutId ? (
              <PrimaryButton className="!min-h-[50px] shadow-[0_8px_28px_rgba(34,197,94,0.2)]" fullWidth onClick={() => nav(`/workouts/history/${todayWorkoutId}`)}>
                View today&apos;s session
              </PrimaryButton>
            ) : null}
            <button
              type="button"
              onClick={startSession}
              className="text-center text-xs font-medium text-[var(--text-muted)] underline-offset-2 hover:text-[var(--text-secondary)] hover:underline"
            >
              Log another session
            </button>
          </div>
        </PremiumCard>
      ) : recoveryDay ? (
        <PremiumCard variant="sleep" className="!p-5 shadow-[0_12px_40px_rgba(99,102,241,0.12)]">
          <p className="text-[10px] font-bold uppercase tracking-widest text-indigo-300/90">Recovery day</p>
          <h2 className="mt-1 font-display text-2xl font-bold text-[var(--text-primary)]">Rest is part of progress</h2>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">Mobility, hydration, and sleep count today — or log training if you&apos;re feeling good.</p>
          <div className="mt-5 flex flex-col gap-2">
            <PrimaryButton className="!min-h-[54px] !text-base shadow-[0_10px_32px_rgba(0,102,255,0.35)]" fullWidth onClick={startSession}>
              Log workout
            </PrimaryButton>
            <PrimaryButton variant="secondary" className="!min-h-[48px]" fullWidth onClick={startRecoveryLog}>
              Log recovery / mobility
            </PrimaryButton>
          </div>
        </PremiumCard>
      ) : todaySession ? (
        <PremiumCard variant="train" className="!p-5 shadow-[0_16px_48px_rgba(56,189,248,0.18)]">
          <p className="text-[10px] font-bold uppercase tracking-widest text-sky-300/90">Ready to lift?</p>
          <h2 className="mt-1 font-display text-2xl font-bold tracking-tight text-[var(--text-primary)]">
            {todaySession.focus || todaySession.day}
          </h2>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">Start a session or follow today&apos;s plan.</p>
          <p className="mt-1 text-xs text-[var(--text-muted)]">
            ~{todaySession.duration} min · {todaySession.exercises} exercises · {todaySession.sets} sets
          </p>
          {lastWorkoutHint ? (
            <p className="mt-2 text-[11px] font-medium text-sky-300/80">{lastWorkoutHint}</p>
          ) : null}
          {streak > 0 ? (
            <span className="mt-3 inline-flex rounded-full border border-amber-400/25 bg-amber-500/10 px-2.5 py-0.5 text-[10px] font-bold text-amber-200">
              {streak}-day streak
            </span>
          ) : null}
          <PrimaryButton className="mt-5 !min-h-[54px] !text-base shadow-[0_10px_32px_rgba(0,102,255,0.35)]" fullWidth onClick={startSession}>
            Start workout
          </PrimaryButton>
        </PremiumCard>
      ) : (
        <PremiumCard variant="train" className="!p-5 shadow-[0_16px_48px_rgba(56,189,248,0.18)]">
          <p className="text-[10px] font-bold uppercase tracking-widest text-sky-300/90">Ready to lift?</p>
          <h2 className="mt-1 font-display text-2xl font-bold text-[var(--text-primary)]">Start now</h2>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">Build structure later.</p>
          {lastWorkoutHint ? (
            <p className="mt-2 text-[11px] font-medium text-sky-300/80">{lastWorkoutHint}</p>
          ) : null}
          {streak > 0 ? (
            <span className="mt-3 inline-flex rounded-full border border-amber-400/25 bg-amber-500/10 px-2.5 py-0.5 text-[10px] font-bold text-amber-200">
              {streak}-day streak
            </span>
          ) : null}
          <PrimaryButton className="mt-5 !min-h-[54px] !text-base shadow-[0_10px_32px_rgba(0,102,255,0.35)]" fullWidth onClick={startSession}>
            Start workout
          </PrimaryButton>
          <button
            type="button"
            onClick={() => nav('/workouts/program')}
            className="mt-2.5 w-full text-center text-[11px] font-medium text-[var(--text-muted)] underline-offset-2 hover:text-accent hover:underline"
          >
            Build a program
          </button>
        </PremiumCard>
      )}

      {/* 2 — Today dashboard */}
      <PremiumCard className="!border-white/8 !bg-white/[0.025] !p-3.5 !shadow-none">
        <p className="mb-2.5 text-[10px] font-bold uppercase tracking-[0.16em] text-[var(--text-muted)]">Today</p>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <TodayStatTile
            icon="today"
            label="Today"
            value={todayLabel}
            accent={todayComplete ? 'text-emerald-300' : recoveryDay ? 'text-indigo-300' : 'text-sky-300'}
          />
          <TodayStatTile icon="flame" label="Burned" value={burnedLabel} detail={burnedDetail} accent={todayKcal > 0 ? 'text-orange-300' : undefined} />
          {isNativeIOS() ? (
            <TodayStatTile
              icon="steps"
              label="Steps"
              value={stepsLabel}
              detail={healthConnected ? 'Apple Health' : undefined}
              accent={healthSteps != null && healthSteps > 0 ? 'text-sky-300' : undefined}
            />
          ) : null}
          <TodayStatTile icon="streak" label="Streak" value={streakLabel} accent={streak > 0 ? 'text-amber-300' : undefined} />
          {!isNativeIOS() ? <TodayStatTile icon="schedule" label="Schedule" value={scheduleLabel} /> : null}
        </div>
        {isNativeIOS() ? (
          <button
            type="button"
            disabled={healthSyncing || healthAuthorized}
            onClick={() => {
              if (healthAuthorized) return;
              void (async () => {
                setHealthSyncing(true);
                const safety = window.setTimeout(() => setHealthSyncing(false), 60_000);
                try {
                  const result = await syncAppleHealth({ force: true });
                  setHealthSteps(result.steps);
                  setHealthActiveCalories(result.activeCalories);
                  setHealthAuthorized(true);
                  if (result.importedWorkouts > 0) refresh();
                  showHealthToast(formatHealthSyncSuccess(result));
                } catch (e) {
                  showHealthToast(e instanceof Error ? e.message : 'Could not sync Apple Health.');
                } finally {
                  window.clearTimeout(safety);
                  setHealthSyncing(false);
                }
              })();
            }}
            className={`mt-3 w-full text-center text-[11px] font-medium underline-offset-2 disabled:cursor-default disabled:no-underline ${
              healthAuthorized
                ? 'text-emerald-400/90 opacity-90'
                : 'text-[var(--text-muted)] hover:text-accent hover:underline disabled:opacity-50'
            }`}
          >
            {healthSyncing
              ? 'Syncing Apple Health…'
              : healthAuthorized
                ? 'Synced with Apple Health'
                : 'Connect Apple Health'}
          </button>
        ) : null}
        {healthToast ? (
          <p className="mt-2 text-center text-[11px] text-[var(--text-secondary)]">{healthToast}</p>
        ) : null}
      </PremiumCard>

      {/* 3 — Program (slim) */}
      <div className="flex items-center justify-between gap-3 rounded-2xl border border-white/[0.06] bg-white/[0.02] px-3.5 py-2.5">
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">Program</p>
          {activeGuide ? (
            <>
              <p className="truncate text-sm font-semibold text-[var(--text-primary)]">{activeGuide.title}</p>
              <p className="truncate text-[11px] text-[var(--text-muted)]">
                {todaySession
                  ? `Next · ${todaySession.focus || todaySession.day}`
                  : planRestDay
                    ? 'Rest day in plan'
                    : 'Active block'}
              </p>
            </>
          ) : (
            <p className="text-sm text-[var(--text-muted)]">None selected</p>
          )}
        </div>
        <Link to="/workouts/program" className="shrink-0 text-xs font-semibold text-[var(--text-muted)] hover:text-accent">
          {activeGuide ? 'View →' : 'Create →'}
        </Link>
      </div>

      {/* 4 — Last workout */}
      {lastWorkout ? (
        <LastWorkoutSummaryCard workout={lastWorkout} imperial={imperial} />
      ) : (
        <div className="rounded-2xl border border-dashed border-white/8 px-3.5 py-3 text-center text-xs text-[var(--text-muted)]">
          No workouts logged yet
        </div>
      )}

      {/* 5 — This week */}
      <PremiumCard className="!border-white/8 !bg-white/[0.025] !p-3.5 !shadow-none">
        <div className="mb-2 flex items-baseline justify-between gap-2">
          <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-[var(--text-muted)]">This week</p>
          <p className="text-xs font-semibold tabular-nums text-[var(--text-secondary)]">
            {weekWorkouts.length} / {scheduleTarget} sessions
          </p>
        </div>
        <div className="mb-3 h-1.5 overflow-hidden rounded-full bg-white/[0.06]">
          <div
            className={`h-full rounded-full transition-all duration-500 ${
              recoveryDay ? 'bg-indigo-400/50' : 'bg-gradient-to-r from-sky-600 to-sky-400'
            }`}
            style={{ width: `${Math.max(weekProgress * 100, weekWorkouts.length > 0 ? 6 : 0)}%` }}
          />
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">Volume</p>
            <p className="mt-0.5 font-bold tabular-nums text-[var(--text-primary)]">{weekVolKg > 0 ? weekVolLabel : '—'}</p>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">Sets</p>
            <p className="mt-0.5 font-bold tabular-nums text-[var(--text-primary)]">{weekSets > 0 ? weekSets : '—'}</p>
          </div>
        </div>
        {recoveryDay ? (
          <p className="mt-2.5 text-[11px] text-indigo-300/80">Recovery day — weekly pace is on track.</p>
        ) : null}
      </PremiumCard>

      {/* 6 — History */}
      <div className="flex justify-center pt-0.5 pb-1">
        <Link
          to="/workouts/history"
          className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.04] px-4 py-2 text-xs font-semibold text-[var(--text-secondary)] transition-colors hover:border-white/16 hover:bg-white/[0.07] hover:text-[var(--text-primary)]"
        >
          View history
          <span aria-hidden>→</span>
        </Link>
      </div>
    </PremiumPage>
  );
}
